import { http } from '@/utils/http';
import { unwrap, buildCacheKey, refreshVoiceCache } from '@/utils/apiClient';

// 情感信息接口
export interface EmotionInfo {
  name: string;      // 中文显示名称
  value: string;     // 微软原始值
  audio_url: string; // 试听音频URL
}

// 角色信息接口
export interface RoleInfo {
  name: string;      // 中文显示名称
  value: string;     // 微软原始值
  audio_url: string; // 试听音频URL
}

// 主播响应接口
export interface VoiceResponse {
  id: number;
  shortName: string;
  name: string;
  localName?: string;
  locale: string;
  language: string;      // 语言代码：zh, en, ar, de, es, fr, hi, ko, th, vi 等
  dialect?: string;      // 方言信息（仅中文主播有）：mandarin, cantonese, taiwanese 等
  label?: string;        // locale 的中文显示名称：中文(普通话，简体)、英语(美国)、阿拉伯语(沙特阿拉伯) 等
  gender: string;
  description?: string;
  isPro: boolean;
  avatar?: string;
  styles?: string[];
  tags: string[];        // 标签数组：["女声", "温柔", "普通话"] 等
  sampleUrl?: string;
  isFavorite?: boolean;
  emo_style_info?: EmotionInfo[]; // 情感风格信息
  role_info?: RoleInfo[];         // 角色信息
}

// 分类响应接口
export interface CategoryResponse {
  id: string;
  label: string;
  voices: VoiceResponse[];
}



// API响应格式
export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

// Tag 对象接口
export interface Tag {
  id: number;
  name: string;
  sort: number;
  type: number;
}

// 简化的主播列表响应接口
export interface VoiceListResponse {
  tagList: Tag[];        // 后端返回的是 Tag 对象数组
  voices: VoiceResponse[];
}

// 主播API - 简化版本，只保留一个接口
export const voiceApi = {
  // 获取指定语种的主播数据（type: 1=中文，2=英文，3=其他语言）
  getVoicesByType: (type: '1' | '2' | '3' = '1', forceRefresh = false) => {
    const options = forceRefresh ? {} : {
      cache: {
        key: buildCacheKey('/api/v1/voices', { type }),
        ttl: 5 * 60 * 1000, // 5分钟缓存
        persist: true,
        swr: true
      }
    };

    return unwrap<VoiceListResponse>(() =>
      http.get('/api/v1/voices', { params: { type } }),
      options
    );
  },

  // 根据ID获取主播
  getById: (id: number) => {
    return unwrap<VoiceResponse>(() =>
      http.get(`/api/v1/voices/${id}`),
      {
        cache: {
          key: `voices:detail:${id}`,
          ttl: 10 * 60 * 1000, // 10分钟缓存
          persist: true,
          swr: true
        }
      }
    );
  },

  // 根据短名称获取主播
  getByShortName: (shortName: string) => {
    return unwrap<VoiceResponse>(() =>
      http.get(`/api/v1/voices/name/${shortName}`),
      {
        cache: {
          key: `voices:name:${shortName}`,
          ttl: 10 * 60 * 1000, // 10分钟缓存
          persist: true,
          swr: true
        }
      }
    );
  },

  // 切换收藏状态（不缓存，因为是状态变更操作）
  toggleFavorite: (id: number) => {
    return unwrap<{ isFavorite: boolean }>(() =>
      http.post(`/api/v1/voices/${id}/favorite`)
    );
  },

  // 获取收藏的主播
  getFavorites: () => {
    return unwrap<VoiceResponse[]>(() =>
      http.get('/api/v1/voices/favorites')
    );
  },

  // 刷新主播数据缓存
  refreshCache: () => {
    refreshVoiceCache();
  }
};

export default voiceApi;
